import React, { useImperativeHandle, useLayoutEffect, useState } from "react";

import { mdiMenuDown } from "@mdi/js";
import Icon from "@mdi/react";
import { FocusScope } from "@radix-ui/react-focus-scope";
import classNames from "classnames";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import useOutsideClick from "../../hooks/useOutsideClick";
import matchesSearch from "../../utils/search";

import type { JSX } from "react";

export interface DropdownItem<T> {
  value: T;
  label: string | JSX.Element;
  searchValue?: string;
}

export interface DropdownRef {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

interface DropdownProps<T> {
  label?: string;
  icon?: JSX.Element;
  items: DropdownItem<T>[];
  disabled?: boolean;
  onClick?: (value: T) => void;
  isNotCloseOnClick?: boolean;
  withoutChevron?: boolean;
  withoutTrigger?: boolean;
  externalRef?: React.RefObject<HTMLElement>;
  className?: string;
  align?: "left" | "right";
  title?: string;
  withSearch?: boolean;
  searchPlaceholder?: string;
  noResultsText?: string;
  maxHeight?: number;
}

function DropdownInner<T>(
  {
    label,
    icon,
    items,
    disabled,
    onClick,
    isNotCloseOnClick,
    withoutChevron,
    withoutTrigger,
    externalRef,
    className,
    align,
    title,
    withSearch,
    searchPlaceholder,
    noResultsText,
    maxHeight = 500,
  }: DropdownProps<T>,
  ref: React.ForwardedRef<DropdownRef>,
): JSX.Element {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const [constrainedMaxHeight, setConstrainedMaxHeight] = useState<
    number | undefined
  >(undefined);
  const [search, setSearch] = useState("");
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const outsideRef = useOutsideClick((event) => {
    if (dropdownRef.current?.contains(event)) return;
    if (externalRef?.current?.contains(event)) return;

    if (isOpen) {
      setIsOpen(false);
    }
  });

  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
  }));

  const containerClassName = classNames(
    "flex",
    "items-center",
    "rounded-[0.3125rem]",
    "p-[0.46875rem]",
    "text-foreground",
    "drop-shadow",
    "bg-card",
    "gap-[0.46875rem]",
    icon && label ? "pl-[0.78125rem]" : "pl-[0.46875rem]",
    {
      "cursor-pointer": !disabled,
      "cursor-not-allowed": disabled,
      "opacity-60": disabled,
    },
  );

  const dropdownClassName = classNames(
    "absolute z-[1500] pointer-events-auto",
    align === "right" ? "right-0" : "left-0",
    className,
  );

  const handleClick = (value: T): void => {
    if (onClick) {
      onClick(value);
    }
    if (!isNotCloseOnClick) {
      setIsOpen(false);
      setSearch("");
    }
  };

  const filteredItems = withSearch
    ? items.filter((item) => {
        const haystack =
          item.searchValue ??
          (typeof item.label === "string" ? item.label : null);
        return haystack ? matchesSearch(haystack, search) : true;
      })
    : items;

  const { bottom, top, left, right } = (
    externalRef?.current ?? outsideRef.current
  )?.getBoundingClientRect() ?? {
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
  };

  useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current) {
      setOpenAbove(false);
      setConstrainedMaxHeight(undefined);
      return;
    }

    const dropdownHeight = dropdownRef.current.offsetHeight;
    const spaceBelow = window.innerHeight - bottom;
    const spaceAbove = top;
    const GAP = 8;

    if (spaceBelow >= dropdownHeight) {
      setOpenAbove(false);
      setConstrainedMaxHeight(undefined);
    } else if (spaceAbove >= dropdownHeight) {
      setOpenAbove(true);
      setConstrainedMaxHeight(undefined);
    } else if (spaceAbove > spaceBelow) {
      setOpenAbove(true);
      setConstrainedMaxHeight(spaceAbove - GAP * 2);
    } else {
      setOpenAbove(false);
      setConstrainedMaxHeight(spaceBelow - GAP * 2);
    }
  }, [isOpen, bottom, top]);

  function getDropdownPosition(): React.CSSProperties {
    const horizontalPosition = {
      right: align === "right" ? window.innerWidth - right : undefined,
      left: align !== "right" ? left : undefined,
    };

    if (openAbove) {
      return { bottom: window.innerHeight - top + 4, ...horizontalPosition };
    }

    return { top: bottom, ...horizontalPosition };
  }

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className={dropdownClassName}
      onClick={(e) => e.stopPropagation()}
      style={getDropdownPosition()}
    >
      <div
        className="border-border bg-card overflow-x-hidden overflow-y-auto rounded-lg border"
        style={
          constrainedMaxHeight !== undefined
            ? { maxHeight: constrainedMaxHeight }
            : { maxHeight }
        }
      >
        {title || withSearch ? (
          <div className="bg-card sticky top-0 z-10">
            {title ? (
              <div className="border-border bg-muted text-muted-foreground min-w-[225px] border-b px-[0.9375rem] py-[0.625rem] text-[0.78125rem] font-semibold whitespace-nowrap">
                {title}
              </div>
            ) : null}
            {withSearch ? (
              <div className="border-border border-b px-[0.46875rem] py-[0.3125rem]">
                <input
                  ref={(el): void => {
                    el?.focus();
                  }}
                  className="text-muted-foreground focus:border-border-strong w-full rounded border-none px-[0.46875rem] py-[0.234375rem] text-[0.78125rem] tracking-normal outline-none"
                  onChange={(e): void => setSearch(e.target.value)}
                  onClick={(e): void => e.stopPropagation()}
                  placeholder={searchPlaceholder ?? "Search..."}
                  value={search}
                />
              </div>
            ) : null}
          </div>
        ) : null}
        {withSearch && filteredItems.length === 0 ? (
          <div className="text-muted-foreground px-2.5 py-[0.46875rem] text-[0.78125rem]">
            {noResultsText ?? t("noResults")}
          </div>
        ) : null}
        {filteredItems.map((item) => (
          <div
            key={uuidv4()}
            className="text-muted-foreground hover:bg-muted flex w-full cursor-pointer items-center"
            onClick={(): void =>
              !disabled ? handleClick(item.value) : undefined
            }
          >
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div ref={outsideRef}>
      {!withoutTrigger && (
        <div
          className={containerClassName}
          onClick={(): void => (!disabled ? setIsOpen(!isOpen) : undefined)}
        >
          <div className="flex items-center gap-[0.46875rem]">
            {icon ?? null}
            {label ? <span>{label}</span> : null}
          </div>
          {!withoutChevron && <Icon path={mdiMenuDown} size={1} />}
        </div>
      )}
      {isOpen
        ? ReactDOM.createPortal(
            <FocusScope>{dropdownContent}</FocusScope>,
            document.body,
          )
        : null}
    </div>
  );
}

const Dropdown = React.forwardRef(DropdownInner) as <T>(
  props: DropdownProps<T> & { ref?: React.Ref<DropdownRef> },
) => JSX.Element;

export default Dropdown;
